import { useCallback } from "react";
import { createApiResponse, createErrorApiResponse } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse, ListWithTotal, RangeRequestParams } from "@entrilia/shared/api/types";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { AnyObject } from "@entrilia/shared/types";

export interface CreatorCounts {
  all: number;
  createdByMe: number;
}

interface CreatorCountRequest {
  range: RangeRequestParams;
  createdByMe: boolean;
}

const countOnlyRange = { take: 0, totals: true };

const toCountsResponse = (
  allResponse: ApiResponse<ListWithTotal<unknown>>,
  createdByMeResponse: ApiResponse<ListWithTotal<unknown>>
): ApiResponse<CreatorCounts> => {
  if (!allResponse.success || !createdByMeResponse.success) {
    return createErrorApiResponse(allResponse.error ?? createdByMeResponse.error);
  }
  return createApiResponse({
    all: allResponse.data?.total ?? 0,
    createdByMe: createdByMeResponse.data?.total ?? 0,
  });
};

const useWorkbookCreatorCounts = <P extends AnyObject>(
  search: (request: P & CreatorCountRequest, signal?: AbortSignal) => Promise<ApiResponse<ListWithTotal<unknown>>>,
  filterPayload: P
) => {
  const getCounts = useCallback(
    async (signal?: AbortSignal) => {
      const [allResponse, createdByMeResponse] = await Promise.all([
        search({ ...filterPayload, range: countOnlyRange, createdByMe: false }, signal),
        search({ ...filterPayload, range: countOnlyRange, createdByMe: true }, signal),
      ]);
      return toCountsResponse(allResponse, createdByMeResponse);
    },
    [search, filterPayload]
  );

  const [counts, fetchError, { fetchSilent }] = useFetch(getCounts);

  if (fetchError) {
    logError(fetchError, "[useWorkbookCreatorCounts] search");
  }

  return { counts: fetchError ? undefined : counts, refreshCounts: fetchSilent };
};

export default useWorkbookCreatorCounts;
